import React, { useState, useEffect, useRef } from 'react';
import { Dropdown, Badge, ListGroup, Button, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Bell, CircleCheck, Clock, TriangleAlert, FileText, MessageSquare, Info, CheckCheck, Trash2 } from 'lucide-react';
import { useNotifications } from '../../contexts/NotificationContext';
import './NotificationDropdown.css';

const NotificationDropdown = () => {
    const navigate = useNavigate();
    const [show, setShow] = useState(false);
    const dropdownRef = useRef(null);

    const {
        notifications,
        unreadCount,
        loading,
        fetchRecentNotifications,
        markAsRead,
        markAllAsRead,
        deleteNotification
    } = useNotifications();

    // 드롭다운 열릴 때 알림 가져오기
    useEffect(() => {
        if (show) {
            fetchRecentNotifications();
        }
    }, [show, fetchRecentNotifications]);

    // 알림 유형별 아이콘
    const getNotificationIcon = (type) => {
        const icons = {
            grading_complete: <CircleCheck style={{ color: 'var(--status-success)' }} size="1em" />,
            assignment_due_soon: <Clock style={{ color: 'var(--status-warning)' }} size="1em" />,
            assignment_overdue: <TriangleAlert style={{ color: 'var(--status-danger)' }} size="1em" />,
            new_assignment: <FileText style={{ color: 'var(--status-info)' }} size="1em" />,
            new_submission: <FileText style={{ color: 'var(--brand-500)' }} size="1em" />,
            feedback_received: <MessageSquare style={{ color: 'var(--status-success)' }} size="1em" />,
            system: <Info style={{ color: 'var(--status-neutral)' }} size="1em" />
        };
        return icons[type] || icons.system;
    };

    // 시간 포맷
    const formatTime = (date) => {
        const now = new Date();
        const diff = now - new Date(date);
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(diff / 3600000);
        const days = Math.floor(diff / 86400000);

        if (minutes < 1) return '방금 전';
        if (minutes < 60) return `${minutes}분 전`;
        if (hours < 24) return `${hours}시간 전`;
        if (days < 7) return `${days}일 전`;
        return new Date(date).toLocaleDateString('ko-KR');
    };

    // 알림 클릭 처리
    const handleNotificationClick = async (notification) => {
        if (!notification.isRead) {
            await markAsRead(notification._id);
        }

        setShow(false);

        if (notification.actionUrl) {
            navigate(notification.actionUrl);
        }
    };

    // 삭제 처리
    const handleDelete = async (e, notificationId) => {
        e.stopPropagation();
        await deleteNotification(notificationId);
    };

    return (
        <Dropdown
            show={show}
            onToggle={(isOpen) => setShow(isOpen)}
            align="end"
            ref={dropdownRef}
        >
            <Dropdown.Toggle
                variant="link"
                id="notification-dropdown"
                className="notification-toggle"
            >
                <Bell size={20} />
                {unreadCount > 0 && (
                    <Badge
                        bg="danger"
                        pill
                        className="notification-badge"
                    >
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </Badge>
                )}
            </Dropdown.Toggle>

            <Dropdown.Menu className="notification-menu">
                <div className="notification-header">
                    <h6 className="mb-0">알림</h6>
                    {unreadCount > 0 && (
                        <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-muted"
                            onClick={markAllAsRead}
                        >
                            <CheckCheck className="me-1" size="1em" />
                            모두 읽음
                        </Button>
                    )}
                </div>

                <div className="notification-body">
                    {loading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" size="sm" />
                        </div>
                    ) : notifications.length > 0 ? (
                        <ListGroup variant="flush">
                            {notifications.slice(0, 5).map((notification) => (
                                <ListGroup.Item
                                    key={notification._id}
                                    action
                                    onClick={() => handleNotificationClick(notification)}
                                    className={`notification-item ${!notification.isRead ? 'unread' : ''}`}
                                >
                                    <div className="d-flex">
                                        <div className="notification-icon me-3">
                                            {getNotificationIcon(notification.type)}
                                        </div>
                                        <div className="flex-grow-1">
                                            <div className="d-flex justify-content-between align-items-start">
                                                <strong className="notification-title">
                                                    {notification.title}
                                                </strong>
                                                <Button
                                                    variant="link"
                                                    size="sm"
                                                    className="p-0 text-muted notification-delete"
                                                    onClick={(e) => handleDelete(e, notification._id)}
                                                >
                                                    <Trash2 size={14} />
                                                </Button>
                                            </div>
                                            <p className="notification-message mb-1">
                                                {notification.message}
                                            </p>
                                            <small className="notification-time text-muted">
                                                {formatTime(notification.createdAt)}
                                            </small>
                                        </div>
                                    </div>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    ) : (
                        <div className="text-center py-4 text-muted">
                            <Bell size={32} className="mb-2 opacity-50" />
                            <p className="mb-0">알림이 없습니다</p>
                        </div>
                    )}
                </div>

                {notifications.length > 0 && (
                    <div className="notification-footer">
                        <Button
                            variant="link"
                            size="sm"
                            className="w-100"
                            onClick={() => {
                                setShow(false);
                                navigate('/notifications');
                            }}
                        >
                            모든 알림 보기
                        </Button>
                    </div>
                )}
            </Dropdown.Menu>
        </Dropdown>
    );
};

export default NotificationDropdown;
